import React from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';

interface AssetAttribution {
  symbol: string;
  return: number;
  allocation: number;
  contribution: number;
}

interface StrategyAttribution {
  strategyName: string;
  return: number;
  trades: number;
  contribution: number;
}

interface BreakdownData {
  byAsset: AssetAttribution[];
  byStrategy: StrategyAttribution[];
}

interface AttributionBreakdownProps {
  breakdown?: BreakdownData;
}

const AttributionBreakdown: React.FC<AttributionBreakdownProps> = ({ breakdown }) => {
  if (!breakdown || (!breakdown.byAsset.length && !breakdown.byStrategy.length)) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
        <div className="text-center">
          <div className="text-sm text-gray-600 space-y-2">
            <p>📈 No trading activity yet</p>
            <p className="text-xs">Make some trades to see performance attribution analysis</p>
          </div>
        </div>
      </div>
    );
  }

  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

  const formatPercentage = (value: number | string | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    return `${numValue.toFixed(2)}%`;
  };
  const formatContribution = (value: number | string | null | undefined) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value as string) || 0;
    return `${numValue >= 0 ? '+' : ''}${numValue.toFixed(2)}%`;
  };

  // Prepare pie chart data for asset allocation
  const assetPieData = breakdown.byAsset.map((asset, index) => ({
    name: asset.symbol,
    value: Math.abs(asset.allocation),
    contribution: asset.contribution,
    color: COLORS[index % COLORS.length]
  }));

  // Prepare bar chart data for contribution analysis
  const contributionBarData = [
    ...breakdown.byAsset.map(asset => ({
      name: asset.symbol,
      contribution: asset.contribution,
      type: 'Asset',
      return: asset.return
    })),
    ...breakdown.byStrategy.map(strategy => ({
      name: strategy.strategyName,
      contribution: strategy.contribution,
      type: 'Strategy',
      return: strategy.return
    }))
  ].sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-md">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          <p className="text-sm text-gray-600">
            Contribution: <span className="font-medium">{formatContribution(data.contribution)}</span>
          </p>
          {data.return !== undefined && (
            <p className="text-sm text-gray-600">
              Return: <span className="font-medium">{formatPercentage(data.return)}</span>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-md">
          <p className="font-medium text-gray-900 mb-1">{data.name}</p>
          <p className="text-sm text-gray-600">
            Allocation: <span className="font-medium">{formatPercentage(data.value)}</span>
          </p>
          <p className="text-sm text-gray-600">
            Contribution: <span className="font-medium">{formatContribution(data.contribution)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Asset Attribution */}
      {breakdown.byAsset.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900">Attribution by Asset</h4>
          
          {/* Asset Allocation Pie Chart */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-3">Current Allocation</h5>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={assetPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {assetPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<PieTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            
            {/* Asset Details Table */}
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-3">Performance Breakdown</h5>
              <div className="space-y-2">
                {breakdown.byAsset.map((asset, index) => (
                  <div key={asset.symbol} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div className="flex items-center space-x-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      ></div>
                      <span className="text-sm font-medium text-gray-900">{asset.symbol}</span>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPercentage(asset.allocation)}
                      </div>
                      <div className={`text-xs ${asset.contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatContribution(asset.contribution)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Strategy Attribution */}
      {breakdown.byStrategy.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900">Attribution by Strategy</h4>
          
          <div className="grid grid-cols-1 gap-4">
            {breakdown.byStrategy.map((strategy) => (
              <div key={strategy.strategyName} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h5 className="text-sm font-medium text-gray-900">{strategy.strategyName}</h5>
                  <div className="flex items-center space-x-2">
                    {strategy.contribution >= 0 ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`text-sm font-medium ${strategy.contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatContribution(strategy.contribution)}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {formatPercentage(strategy.return)}
                    </div>
                    <div className="text-xs text-gray-500">Return</div>
                  </div>
                  <div>
                    <div className="text-lg font-semibold text-gray-900">
                      {strategy.trades}
                    </div>
                    <div className="text-xs text-gray-500">Trades</div>
                  </div>
                  <div>
                    <div className={`text-lg font-semibold ${strategy.contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatContribution(strategy.contribution)}
                    </div>
                    <div className="text-xs text-gray-500">Contribution</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Combined Contribution Chart */}
      {contributionBarData.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-sm font-medium text-gray-900">Overall Contribution Analysis</h4>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={contributionBarData.slice(0, 8)} // Show top 8 contributors
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis 
                  dataKey="name" 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis 
                  stroke="#666"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="contribution" 
                  fill="#3b82f6"
                  name="Contribution"
                  radius={[2, 2, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Summary Statistics */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-900 mb-3">Attribution Summary</h4>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {breakdown.byAsset.length}
            </div>
            <div className="text-xs text-gray-500">Assets</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {breakdown.byStrategy.length}
            </div>
            <div className="text-xs text-gray-500">Strategies</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-green-600">
              {formatContribution(
                [...breakdown.byAsset, ...breakdown.byStrategy]
                  .filter(item => item.contribution > 0)
                  .reduce((sum, item) => sum + item.contribution, 0)
              )}
            </div>
            <div className="text-xs text-gray-500">Total Gains</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-red-600">
              {formatContribution(
                [...breakdown.byAsset, ...breakdown.byStrategy]
                  .filter(item => item.contribution < 0)
                  .reduce((sum, item) => sum + item.contribution, 0)
              )}
            </div>
            <div className="text-xs text-gray-500">Total Losses</div>
          </div>
        </div>
      </div>

      {/* Top Contributors */}
      {contributionBarData.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-900">Top Contributors</h4>
          <div className="space-y-2">
            {contributionBarData.slice(0, 5).map((item, index) => (
              <div key={`${item.name}-${item.type}`} className="flex items-center justify-between p-2 bg-white border rounded">
                <div className="flex items-center space-x-3">
                  <div className="w-6 h-6 bg-blue-100 rounded flex items-center justify-center text-xs font-medium text-blue-800">
                    {index + 1}
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-900">{item.name}</span>
                    <span className="text-xs text-gray-500 ml-2">({item.type})</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-sm font-medium ${item.contribution >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatContribution(item.contribution)}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatPercentage(item.return)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributionBreakdown; 